import React, {
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import {
  AdminButton,
} from "../../../pages/admin/action_engine";
import {
  AdminFormField,
  AdminFormSection,
  createAdminFormFieldDescriptor,
  validateAdminFormFieldValue,
} from "../../../pages/admin/section_engine";


const INITIAL_VALUES = Object.freeze({
  name: "",
  supplier_code: "",
  legal_name: "",
  trade_name: "",
  country_code: "",
  default_currency: "EUR",
  website_url: "",
  notes: "",
  connector_type: "",
  connector_endpoint: "",
  sftp_host: "",
  order_email: "",
  minimum_order_amount: 0,
  franco_amount: 0,
  is_active: true,
});


function optionalText(value) {
  if (
    value === null
    || value === undefined
  ) {
    return undefined;
  }

  const normalized =
    String(value).trim();

  return normalized || undefined;
}


export function buildSupplierCreatePayload(
  values,
) {
  const payload = {
    name: String(values.name || "").trim(),
    is_active: Boolean(values.is_active),
    minimum_order_amount:
      Number(values.minimum_order_amount || 0),
    franco_amount:
      Number(values.franco_amount || 0),
  };

  [
    "supplier_code",
    "legal_name",
    "trade_name",
    "country_code",
    "default_currency",
    "website_url",
    "notes",
    "connector_type",
    "connector_endpoint",
    "sftp_host",
    "order_email",
  ].forEach((fieldName) => {
    const value =
      optionalText(values[fieldName]);

    if (value !== undefined) {
      payload[fieldName] = value;
    }
  });

  return payload;
}


export default function SupplierCreateForm({
  onSubmit,
  onCancel,
  loading = false,
  error = "",
}) {
  const [values, setValues] =
    useState(() => ({
      ...INITIAL_VALUES,
    }));

  const [errors, setErrors] =
    useState({});

  const fields = useMemo(
    () => [
      createAdminFormFieldDescriptor({
        name: "name",
        label: "Nom du fournisseur",
        type: "text",
        required: true,
        placeholder: "Ex. Asmodee",
      }),
      createAdminFormFieldDescriptor({
        name: "supplier_code",
        label: "Code fournisseur",
        type: "text",
        description:
          "Code interne ou code fournisseur.",
      }),
      createAdminFormFieldDescriptor({
        name: "legal_name",
        label: "Raison sociale",
        type: "text",
      }),
      createAdminFormFieldDescriptor({
        name: "trade_name",
        label: "Nom commercial",
        type: "text",
      }),
      createAdminFormFieldDescriptor({
        name: "country_code",
        label: "Pays",
        type: "text",
        placeholder: "FR",
        validate: (value) => {
          if (!value) {
            return null;
          }

          return /^[A-Za-z]{2}$/.test(
            String(value).trim()
          )
            ? null
            : "Le code pays doit contenir 2 lettres.";
        },
      }),
      createAdminFormFieldDescriptor({
        name: "default_currency",
        label: "Devise",
        type: "text",
        placeholder: "EUR",
        validate: (value) => {
          if (!value) {
            return null;
          }

          return /^[A-Za-z]{3}$/.test(
            String(value).trim()
          )
            ? null
            : "La devise doit contenir 3 lettres.";
        },
      }),
      createAdminFormFieldDescriptor({
        name: "website_url",
        label: "Site web",
        type: "text",
        placeholder: "https://...",
      }),
      createAdminFormFieldDescriptor({
        name: "order_email",
        label: "Email de commande",
        type: "text",
        placeholder: "commandes@fournisseur.fr",
      }),
      createAdminFormFieldDescriptor({
        name: "minimum_order_amount",
        label: "Commande minimum",
        type: "number",
        min: 0,
        step: 0.01,
      }),
      createAdminFormFieldDescriptor({
        name: "franco_amount",
        label: "Franco",
        type: "number",
        min: 0,
        step: 0.01,
      }),
      createAdminFormFieldDescriptor({
        name: "connector_type",
        label: "Type de connecteur",
        type: "select",
        options: [
          {
            value: "",
            label: "Non configuré",
          },
          {
            value: "REST",
            label: "REST",
          },
          {
            value: "SFTP",
            label: "SFTP",
          },
        ],
      }),
      createAdminFormFieldDescriptor({
        name: "connector_endpoint",
        label: "Endpoint connecteur",
        type: "text",
      }),
      createAdminFormFieldDescriptor({
        name: "sftp_host",
        label: "Hôte SFTP",
        type: "text",
      }),
      createAdminFormFieldDescriptor({
        name: "notes",
        label: "Notes",
        type: "textarea",
      }),
      createAdminFormFieldDescriptor({
        name: "is_active",
        label: "Fournisseur actif",
        type: "boolean",
      }),
    ],
    [],
  );

  const handleChange = (
    fieldName,
    value,
  ) => {
    setValues((current) => ({
      ...current,
      [fieldName]: value,
    }));

    setErrors((current) => ({
      ...current,
      [fieldName]: null,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = {};

    fields.forEach((field) => {
      const validationError =
        validateAdminFormFieldValue(
          field,
          values[field.name],
        );

      if (validationError) {
        nextErrors[field.name] =
          validationError;
      }
    });

    if (
      Number(values.minimum_order_amount) < 0
    ) {
      nextErrors.minimum_order_amount =
        "Le montant doit être supérieur ou égal à zéro.";
    }

    if (
      Number(values.franco_amount) < 0
    ) {
      nextErrors.franco_amount =
        "Le montant doit être supérieur ou égal à zéro.";
    }

    setErrors(nextErrors);

    if (
      Object.keys(nextErrors).length
    ) {
      return;
    }

    if (
      typeof onSubmit === "function"
    ) {
      await onSubmit(
        buildSupplierCreatePayload(
          values,
        ),
      );
    }
  };

  return (
    <AdminFormSection>
      <FormHeader>
        <div>
          <h4>Créer un fournisseur</h4>
          <p>
            Le fournisseur sera rattaché à
            la société et à l’activité du
            scope administrateur actif.
          </p>
        </div>
      </FormHeader>

      <Form
        onSubmit={handleSubmit}
        noValidate
      >
        <FieldsGrid>
          {fields.map((field) => (
            <AdminFormField
              key={field.name}
              descriptor={field}
              value={values[field.name]}
              error={errors[field.name]}
              onChange={handleChange}
            />
          ))}
        </FieldsGrid>

        {error ? (
          <FormError role="alert">
            {error}
          </FormError>
        ) : null}

        <Actions>
          <AdminButton
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            Créer le fournisseur
          </AdminButton>

          <AdminButton
            type="button"
            variant="secondary"
            disabled={loading}
            onClick={onCancel}
          >
            Annuler
          </AdminButton>
        </Actions>
      </Form>
    </AdminFormSection>
  );
}


const FormHeader = styled.header`
  margin-bottom: 1rem;

  h4 {
    margin: 0;
  }

  p {
    margin:
      0.35rem 0 0;
    color:
      rgba(247, 247, 247, 0.62);
  }
`;


const Form = styled.form`
  display: grid;
  gap: 1rem;
`;


const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns:
    repeat(
      auto-fit,
      minmax(220px, 1fr)
    );
  gap: 1rem;
`;


const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;


const FormError = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background:
    rgba(225, 95, 95, 0.12);
  color: #ffaaaa;
`;
